import PropTypes from 'prop-types';
import React from 'react';

import {
  COMPONENT_SIZE_DYNAMIC,
  COMPONENT_SIZE_MEDIUM,
  COMPONENT_SIZE_SMALL,
  ComponentSize,
  Orientation,
  ORIENTATION_VERTICAL,
  orientationsList,
} from '../../../typings/design-tokens';
import { teamFormation, teamFormationList } from '../../../typings/formations';
import { IComponent } from '../../../typings/generic-component';
import { formationPlaceList, formationPlaceType } from '../../../typings/player';
import { isPropTypeNullable } from '../../../utils/prop-types';
import { IClassNames, makeClassNames } from '../../../utils/styles';

import { IPlayerInFormation } from './match-formation';
import { createField, createFieldDEBUG } from './match-formation-utils';
import { formations as formationsVertical } from './match-formations-vertical';

import './match-formation-field.scss';

export const componentSizeListCustom: ComponentSize[] = [
  COMPONENT_SIZE_SMALL,
  COMPONENT_SIZE_MEDIUM,
  COMPONENT_SIZE_DYNAMIC,
];
export interface IMatchFormationField extends IComponent {
  customFormationSchema?: IPlayerInFormation[];
  debug?: boolean;
  formation: teamFormation;
  readonly hasImage?: boolean;
  readonly isHome?: boolean;
  readonly orientation?: Orientation;
  readonly players: Array<IFormationPlayer>;
}

export interface IFormationPlayer {
  formationPlace: formationPlaceType;
  fullName: string;
  jerseyNumber: number;
  imageUrl?: string;
  playerPageLink?: string;
}

export const playerNameSlice = (name: string, maxCharLength?: number) =>
  maxCharLength && name.length > maxCharLength ? `${name.slice(0, maxCharLength - 3)}...` : name;

export const getFormationSchema = (fieldOrientation: Orientation) => {
  switch (fieldOrientation) {
    case 'horizontal':
      return formationsVertical; // TODO: change formations to formationsHorizontal when it will be available
    case 'vertical':
      return formationsVertical;
    /* istanbul ignore next: unreachable code */
    default:
      return formationsVertical;
  }
};

const classNames = (size: ComponentSize): IClassNames =>
  makeClassNames({
    MatchFormationField: `match-formation-field--${size}`,
    Player: 'match-formation-player-in-field',
    PlayersContainer: 'match-formation-players-container',
  });

const MatchFormationField: React.FC<IMatchFormationField> = ({
  /* istanbul ignore next: default value */ debug = false,
  /* istanbul ignore next: default value */ formation = '442',
  /* istanbul ignore next: default value */ hasImage = false,
  /* istanbul ignore next: default value */ isHome = true,
  /* istanbul ignore next: default value */ orientation = ORIENTATION_VERTICAL,
  /* istanbul ignore next: default value */ players = [],
  /* istanbul ignore next: default value */ size = COMPONENT_SIZE_DYNAMIC,
  customFormationSchema,
}) => {
  const cn = classNames(size);
  return (
    <div className={cn.MatchFormationField}>
      <div className={cn.PlayersContainer}>
        {debug
          ? createFieldDEBUG({ cn, formation, size, orientation, hasImage, customFormationSchema })
          : createField({
              cn,
              customFormationSchema,
              formation,
              hasImage,
              isHome,
              orientation,
              players,
              size,
            })}
      </div>
    </div>
  );
};

/* istanbul ignore else */ if (process.env.NODE_ENV !== 'production') {
  MatchFormationField.propTypes = {
    customFormationSchema: PropTypes.arrayOf(
      PropTypes.shape({
        formationPlace: PropTypes.oneOf(formationPlaceList).isRequired,
        left: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
        maxCharLength: PropTypes.number.isRequired,
        top: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      }).isRequired
    ),
    debug: PropTypes.bool,
    formation: PropTypes.oneOf(teamFormationList).isRequired,
    hasImage: PropTypes.bool,
    isHome: PropTypes.bool,
    orientation: PropTypes.oneOf(orientationsList),
    players: PropTypes.arrayOf(
      PropTypes.shape({
        formationPlace: PropTypes.oneOf(formationPlaceList).isRequired,
        fullName: PropTypes.string.isRequired,
        imageUrl: isPropTypeNullable(PropTypes.string),
        jerseyNumber: PropTypes.number.isRequired,
        playerPageLink: isPropTypeNullable(PropTypes.string),
      }).isRequired
    ).isRequired,
    size: PropTypes.oneOf(componentSizeListCustom),
  };
}

export default MatchFormationField;
